import { useLocalStorage } from 'usehooks-ts'
import React, { startTransition, useContext, useEffect, useRef, useState } from 'react'
import { LocalStorageKey } from '@/constants/localstorage'
import { Howler } from 'howler'

export enum SOUNDS {
  PLAYER_BET,
  PLAYER_BET_ERROR,
  TIMER_STARTS,
  ROLLING_STARTS,
  ROLLING_STEP,
  PLAYER_WON,
  PLAYER_LOST,
  NEW_ROUND,
  SET_AMOUNT,

  HAND_CHANGE,

  COINFLIP_START,
  COINFLIP_LOSE,
  COINFLIP_WIN,

  RUNES_REVAL,
}

const getSounds: () => Record<SOUNDS, Howl> = () => ({
  [SOUNDS.PLAYER_BET]: new Howl({ src: '/assets/sounds/jackpot-place-bet.mp3', preload: true }),
  [SOUNDS.PLAYER_BET_ERROR]: new Howl({ src: '/assets/sounds/error.mp3', preload: true }),
  [SOUNDS.TIMER_STARTS]: new Howl({ src: '/assets/sounds/timer-start.mp3', preload: true }),
  [SOUNDS.ROLLING_STARTS]: new Howl({ src: '/assets/sounds/spinner-start.mp3', preload: true }),
  [SOUNDS.ROLLING_STEP]: new Howl({ src: '/assets/sounds/spinner-click.mp3', preload: true }),
  [SOUNDS.PLAYER_WON]: new Howl({ src: '/assets/sounds/jackpot-winner.mp3', preload: true }),
  [SOUNDS.PLAYER_LOST]: new Howl({ src: '/assets/sounds/jackpot-loser.mp3', preload: true }),
  [SOUNDS.NEW_ROUND]: new Howl({ src: '/assets/sounds/round-start.mp3', preload: true }),
  [SOUNDS.SET_AMOUNT]: new Howl({ src: '/assets/sounds/set-amount.mp3', preload: true }),

  [SOUNDS.HAND_CHANGE]: new Howl({ src: '/assets/sounds/hand-change.mp3', preload: true }),

  [SOUNDS.COINFLIP_START]: new Howl({ src: '/assets/sounds/coinflip/coinflip_start.mp3', preload: true }),

  [SOUNDS.COINFLIP_LOSE]: new Howl({ src: '/assets/sounds/coinflip/coinflip_lose.mp3', preload: true }),
  [SOUNDS.COINFLIP_WIN]: new Howl({ src: '/assets/sounds/coinflip/coinflip_win.mp3', preload: true }),

  [SOUNDS.RUNES_REVAL]: new Howl({ src: '/assets/images/runes/12.mp3', preload: true }),
})

export const AudioProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isSoundEnabled] = useLocalStorage(LocalStorageKey.Audio, true)
  const [sounds, setSounds] = useState<Record<SOUNDS, Howl> | null>(null)
  const ref = useRef(isSoundEnabled)

  useEffect(() => {
    startTransition(() => {
      setSounds(getSounds())
    })
  }, [])

  useEffect(() => {
    ref.current = isSoundEnabled
    Howler.mute(!isSoundEnabled)
  }, [isSoundEnabled])

  const play = (name: SOUNDS) => {
    if (!ref.current || !sounds) {
      return
    }

    sounds[name].stop()
    sounds[name].volume(1)
    sounds[name].play()
  }

  const pause = (name: SOUNDS) => {
    sounds?.[name].pause()
  }

  const volumeFadeDown = async (name: SOUNDS) => {
    if (!sounds) {
      return
    }

    const id = sounds[name].play()

    sounds[name].fade(0, 1, 2 * 1000, id)

    // sounds[name].pause()
  }

  return <AudioContext.Provider value={{ play, volumeFadeDown, pause }}>{children}</AudioContext.Provider>
}

export const AudioContext = React.createContext({
  play: (_name: any) => {}, // eslint-disable-line
  volumeFadeDown: (_name: any) => {}, // eslint-disable-line
  pause: (_name: any) => {}, // eslint-disable-line
})

export const useAudio = () => useContext(AudioContext)
